/**
 * Dashboard-Editor einer Abteilung (Admin).
 *
 * Widgets einschalten, verschieben, Größe ändern, Voll/Kompakt wählen. Erst
 * „Für alle Mitglieder veröffentlichen" speichert (PUT dashboard {config});
 * „Verwerfen" setzt auf den gespeicherten Stand zurück. Mitglieder können das
 * Layout nicht ändern.
 */
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card } from '../../../components/ui';
import { departmentsApi, type DepartmentDetail } from '../../../lib/departments';
import { sameLayout } from '../../../lib/useDashboardConfig';
import type { DashboardConfig, DashboardLayoutItem, DashboardWidgetSettings } from '../../../lib/api';
import type { Density } from '../../../types';
import { getThemeColors } from '../../../lib/theme';
import { WIDGETS, effectiveDashboardLayout } from '../../dashboard/AdminDashboardPage/helpers';
import { DashboardCanvas } from '../../dashboard/AdminDashboardPage/DashboardCanvas';
import { SettingsPanel } from '../../dashboard/AdminDashboardPage/SettingsPanel';
import { apiErrorMessage } from './shared';

interface Props {
  dark: boolean;
  density: Density;
  dept: DepartmentDetail;
  onSaved: () => void | Promise<void>;
}

/** Gespeicherte Konfiguration → vollständiger Editor-Stand (alle Widgets mit explizitem Status). */
function toEditorConfig(stored: DashboardConfig | null): Required<DashboardConfig> {
  const widgets: Record<string, DashboardWidgetSettings> = {};
  for (const w of WIDGETS) {
    widgets[w.id] = stored?.widgets?.[w.id] ?? { visible: false, mode: 'full' };
  }
  return { layout: effectiveDashboardLayout(stored?.layout), widgets };
}

export function DepartmentDashboardTab({ dark, density, dept, onSaved }: Props) {
  const t = getThemeColors(dark);
  const { t: tr } = useTranslation();
  const base = useMemo(() => toEditorConfig(dept.dashboard_config), [dept.dashboard_config]);
  const [config, setConfig] = useState<Required<DashboardConfig>>(base);
  const [saving, setSaving] = useState(false);
  const [state, setState] = useState<{ kind: 'idle' | 'ok' | 'err'; msg?: string }>({ kind: 'idle' });

  const dirty = !sameLayout(config.layout, base.layout) || JSON.stringify(config.widgets) !== JSON.stringify(base.widgets);

  const widgetSettings = (id: string): DashboardWidgetSettings => config.widgets[id] ?? { visible: false, mode: 'full' };
  const setWidgetSettings = (id: string, s: Partial<DashboardWidgetSettings>) =>
    setConfig(prev => ({ ...prev, widgets: { ...prev.widgets, [id]: { ...widgetSettings(id), ...s } } }));
  const setLayout = (layout: DashboardLayoutItem[]) =>
    setConfig(prev => (sameLayout(prev.layout, layout) ? prev : { ...prev, layout }));

  const publish = async () => {
    setSaving(true);
    setState({ kind: 'idle' });
    try {
      await departmentsApi.saveDashboard(dept.id, config);
      setState({ kind: 'ok' });
      await onSaved();
    } catch (e) {
      setState({ kind: 'err', msg: apiErrorMessage(e, tr('departments.dashboard.save_error')) });
    } finally {
      setSaving(false);
    }
  };

  const discard = () => { setConfig(base); setState({ kind: 'idle' }); };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <Card dark={dark} style={{ padding: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 220 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: t.heading }}>{tr('departments.dashboard.edit_mode')}</div>
            <div style={{ fontSize: 12, color: t.muted }}>
              {tr('departments.dashboard.edit_hint')} {tr('departments.dashboard.applies_to', { count: dept.members.length })}
            </div>
          </div>
          <Button dark={dark} variant="secondary" size="sm" disabled={!dirty || saving} onClick={discard}>
            {tr('departments.dashboard.discard')}
          </Button>
          <Button dark={dark} variant="primary" size="sm" disabled={saving} onClick={() => void publish()}>
            {tr('departments.dashboard.publish')}
          </Button>
        </div>
        {state.kind === 'ok' && <div style={{ fontSize: 12, color: '#027a48', marginTop: 8 }}>{tr('departments.dashboard.published')}</div>}
        {state.kind === 'err' && <div role="alert" style={{ fontSize: 12, color: '#b42318', marginTop: 8 }}>{state.msg}</div>}
      </Card>

      <SettingsPanel dark={dark} widgets={WIDGETS} widgetSettings={widgetSettings} setWidgetSettings={setWidgetSettings} />

      <DashboardCanvas
        dark={dark}
        density={density}
        config={config}
        setPage={() => { /* Im Editor keine Navigation */ }}
        editable
        onLayoutChange={setLayout}
      />

      <div style={{ fontSize: 12, color: t.muted }}>{tr('departments.dashboard.members_cannot_change')}</div>
    </div>
  );
}
